import {
  CORE_STOPS,
  coreGradient,
  FLARE_GLOW_STOPS,
  FLARE_STOPS,
  FULL,
  HALO_STOPS,
  PLANET_HALO_STOPS,
  PLANET_STOPS,
  planetGradient,
  type Stop,
} from './brand-geometry.js';
import { DEPTH_MAX, ORBIT_POINTS, projectOrbit, swayAt, THREE_BODY } from './three-body.js';

/**
 * Отрисовка индикатора «трёх тел» на `<canvas>`.
 *
 * Всё тяжёлое — градиенты ядра, ореола, блика и планет — рисуется один раз в
 * маленькие спрайты (offscreen-canvas) под текущий размер и плотность экрана; на
 * кадр остаются `drawImage` с масштабом и прозрачностью и линии путей за
 * планетами. Буферы точек выделены заранее — в цикле кадра нет аллокаций.
 *
 * Цикл кадров живёт, только пока индикатор на экране (IntersectionObserver) и
 * вкладка видна (requestAnimationFrame сам засыпает в скрытой вкладке).
 */

/** Поле знака занимает середину холста; блик и ореол выходят за него на четверть. */
export const CANVAS_OVERSCAN = 1.5;
/** Мельче этого (CSS px) — только ядро и три точки: линии путей там — рябь. */
const TINY_PX = 32;
/** Ядро и планеты упрощённого индикатора, в единицах поля. */
const TINY = { core: 15, planet: 8 } as const;
/**
 * Индикатор свободнее знака: ядро втрое меньше поля (11 против 20,5 у знака),
 * чтобы вытянутые орбиты — апоцентр в четыре радиуса ядра — уместились в то же
 * поле; планеты крупнее — он живёт на 48–96 px. Блик — короче, под ядро.
 */
export const LOADER = { core: 11, halo: 26, planet: 6.4, planetHalo: 13, flare: 0.75 } as const;
/** Спрайт планеты рисуется с запасом под самую ближнюю (f ≈ 1.35) — не мылится. */
const PLANET_SPRITE_SCALE = 1.4;
/**
 * Линия пути за планетой: столько звеньев по точкам таблицы (96 из 240 —
 * две пятых цикла). Таблица равномерна по времени, поэтому на быстром участке
 * у ядра линия длиннее, вдали — короче. Яркая у планеты, тает к хвосту.
 */
const LINE = 96;
/** Ступени яркости линий: звенья одной ступени — один путь и один `stroke`. */
const LINE_LEVELS = 10;
/** Планеты проявляются при появлении индикатора — без скачка с заставки; линии разворачиваются следом. */
const FADE_IN_MS = 600;

type Sprite = { canvas: HTMLCanvasElement; radiusX: number; radiusY: number };

/** Ступени градиента из `brand-geometry.ts` в canvas-градиент. */
function paint(g: CanvasGradient, stops: readonly Stop[]): CanvasGradient {
  for (const [offset, color, opacity] of stops) {
    const r = parseInt(color.slice(1, 3), 16);
    const gg = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    g.addColorStop(offset, `rgba(${r},${gg},${b},${opacity})`);
  }
  return g;
}

/** Спрайт: поле в единицах (radiusX × radiusY вокруг центра), px — пикселей на единицу. */
function sprite(radiusX: number, radiusY: number, px: number, draw: (ctx: CanvasRenderingContext2D) => void): Sprite {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.ceil(2 * radiusX * px));
  canvas.height = Math.max(1, Math.ceil(2 * radiusY * px));
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.setTransform(canvas.width / (2 * radiusX), 0, 0, canvas.height / (2 * radiusY), canvas.width / 2, canvas.height / 2);
    draw(ctx);
  }
  return { canvas, radiusX, radiusY };
}

function disc(ctx: CanvasRenderingContext2D, r: number, at: { cx: number; cy: number; r: number }, stops: readonly Stop[]): void {
  ctx.fillStyle = paint(ctx.createRadialGradient(at.cx, at.cy, 0, at.cx, at.cy, at.r), stops);
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
}

function spindle(ctx: CanvasRenderingContext2D, halfLength: number, halfWidth: number, stops: readonly Stop[]): void {
  ctx.fillStyle = paint(ctx.createLinearGradient(-halfLength, 0, halfLength, 0), stops);
  ctx.beginPath();
  ctx.moveTo(-halfLength, 0);
  ctx.quadraticCurveTo(0, -2 * halfWidth, halfLength, 0);
  ctx.quadraticCurveTo(0, 2 * halfWidth, -halfLength, 0);
  ctx.fill();
}

type Sprites = {
  halo: Sprite;
  core: Sprite;
  flare: Sprite;
  planet: Sprite;
  tinyCore: Sprite;
  tinyPlanet: Sprite;
};

function buildSprites(px: number): Sprites {
  const pr = LOADER.planet * PLANET_SPRITE_SCALE;
  const ph = LOADER.planetHalo * PLANET_SPRITE_SCALE;
  return {
    halo: sprite(LOADER.halo, LOADER.halo, px, (ctx) => disc(ctx, LOADER.halo, { cx: 0, cy: 0, r: LOADER.halo }, HALO_STOPS)),
    core: sprite(LOADER.core + 0.5, LOADER.core + 0.5, px, (ctx) =>
      disc(ctx, LOADER.core, coreGradient(LOADER.core, 0, 0), CORE_STOPS),
    ),
    flare: sprite(FULL.flare, 2 * FULL.flareGlowW + 0.5, px, (ctx) => {
      spindle(ctx, FULL.flareGlow, FULL.flareGlowW, FLARE_GLOW_STOPS);
      spindle(ctx, FULL.flare, FULL.flareW, FLARE_STOPS);
    }),
    planet: sprite(ph, ph, px, (ctx) => {
      disc(ctx, ph, { cx: 0, cy: 0, r: ph }, PLANET_HALO_STOPS);
      disc(ctx, pr, planetGradient(pr, 0, 0), PLANET_STOPS);
    }),
    tinyCore: sprite(TINY.core + 0.5, TINY.core + 0.5, px, (ctx) =>
      disc(ctx, TINY.core, coreGradient(TINY.core, 0, 0), CORE_STOPS),
    ),
    tinyPlanet: sprite(TINY.planet * 1.4 + 0.5, TINY.planet * 1.4 + 0.5, px, (ctx) =>
      disc(ctx, TINY.planet * 1.4, planetGradient(TINY.planet * 1.4, 0, 0), PLANET_STOPS),
    ),
  };
}

/** Спрайт с центром в (x, y) и масштабом `scale` (1 — как построен). */
function stamp(ctx: CanvasRenderingContext2D, s: Sprite, x: number, y: number, scaleX: number, scaleY = scaleX): void {
  const w = s.radiusX * scaleX;
  const h = s.radiusY * scaleY;
  ctx.drawImage(s.canvas, x - w, y - h, 2 * w, 2 * h);
}

const smoothstep = (a: number, b: number, x: number): number => {
  const t = Math.min(1, Math.max(0, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
};

/** Ближе — ярче: 0 у самой дальней точки, 1 у самой ближней. */
const nearness = (z: number): number => Math.min(1, Math.max(0, (z + DEPTH_MAX) / (2 * DEPTH_MAX)));

function reducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

/**
 * Запускает индикатор на `canvas` внутри `host` (host задаёт размер поля
 * знака). Возвращает остановку.
 */
export function mountSunLoader(host: HTMLElement, canvas: HTMLCanvasElement): () => void {
  let ctx: CanvasRenderingContext2D | null = null;
  try {
    ctx = canvas.getContext('2d');
  } catch {
    ctx = null;
  }
  if (!ctx) return () => {};
  const g = ctx;

  const still = reducedMotion();
  const start = performance.now();
  // Буферы точек: [x, y, z, f] на точку.
  const planets = new Float64Array(3 * 4);
  const line = new Float64Array(3 * (LINE + 1) * 4);
  // Ступень яркости каждого звена (−1 — звена нет): считается раз за кадр.
  const level = new Int8Array(3 * LINE);
  const order = [0, 1, 2];

  let sprites: Sprites | null = null;
  let box = 0;
  let unit = 1;
  let raf = 0;
  let visible = true;

  function layout(): void {
    const size = host.clientWidth || host.getBoundingClientRect().width;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    if (size <= 0) return;
    const px = Math.round(size * CANVAS_OVERSCAN * dpr);
    if (size === box && canvas.width === px) return;
    box = size;
    canvas.width = px;
    canvas.height = px;
    unit = (size * dpr) / 100;
    sprites = buildSprites(unit);
  }

  function drawPlanet(s: Sprites, i: number, alpha: number, tiny: boolean): void {
    if (alpha <= 0.002) return;
    g.globalAlpha = alpha;
    stamp(g, tiny ? s.tinyPlanet : s.planet, planets[i * 4]!, planets[i * 4 + 1]!, planets[i * 4 + 3]! / PLANET_SPRITE_SCALE);
  }

  /**
   * Линии путей одной стороны ядра (за ним или перед ним). Яркость звена —
   * его «возраст» (у планеты 1, к хвосту 0) на близость; ступенями, чтобы
   * звенья одной яркости шли одним путём.
   */
  function drawLines(front: boolean, fade: number): void {
    g.strokeStyle = '#F7AE2E';
    g.lineCap = 'round';
    g.lineJoin = 'round';
    for (let l = 0; l < LINE_LEVELS; l++) {
      let any = false;
      g.beginPath();
      for (let i = 0; i < 3; i++) {
        for (let k = 0; k < LINE; k++) {
          if (level[i * LINE + k] !== l) continue;
          const a = (i * (LINE + 1) + k) * 4;
          const z = (line[a + 2]! + line[a + 6]!) / 2;
          if ((z >= 0) !== front) continue;
          g.moveTo(line[a]!, line[a + 1]!);
          g.lineTo(line[a + 4]!, line[a + 5]!);
          any = true;
        }
      }
      if (!any) continue;
      const v = (l + 0.5) / LINE_LEVELS;
      g.globalAlpha = fade * (0.08 + 0.8 * v);
      g.lineWidth = 0.5 + 1.6 * v;
      g.stroke();
    }
  }

  function frame(now: number): void {
    raf = 0;
    layout();
    const s = sprites;
    if (!s) return;
    const cycle = THREE_BODY.cycleMs;
    const t = (now % cycle) / cycle;
    // Вдох: 0 → 1 → 0 за цикл, мягко у краёв; блик вспыхивает дважды.
    const breath = 0.5 - 0.5 * Math.cos(2 * Math.PI * t);
    const spark = still ? 1 : 0.5 - 0.5 * Math.cos(4 * Math.PI * t);
    const sway = still ? 0 : swayAt(t);
    const phase = still ? 0 : t;
    const fade = still ? 1 : smoothstep(0, FADE_IN_MS, now - start);
    const tiny = box < TINY_PX;
    // Линии разворачиваются с появления: длина растёт вместе с пройденным путём.
    const drawn = tiny || still ? 0 : Math.min(LINE, Math.floor(((now - start) / cycle) * ORBIT_POINTS));

    g.setTransform(1, 0, 0, 1, 0, 0);
    g.clearRect(0, 0, canvas.width, canvas.height);
    g.setTransform(unit, 0, 0, unit, canvas.width / 2, canvas.height / 2);

    for (let i = 0; i < 3; i++) {
      projectOrbit(i, phase + i / 3, sway, planets, i * 4);
      for (let k = 0; k < LINE; k++) level[i * LINE + k] = -1;
      if (drawn > 0) {
        // Точки линии — от планеты назад по пройденному пути.
        for (let k = 0; k <= drawn; k++) projectOrbit(i, phase + i / 3 - k / ORBIT_POINTS, sway, line, (i * (LINE + 1) + k) * 4);
        for (let k = 0; k < drawn; k++) {
          const a = (i * (LINE + 1) + k) * 4;
          const age = 1 - k / LINE;
          const near = nearness((line[a + 2]! + line[a + 6]!) / 2);
          const v = age * age * (0.35 + 0.65 * near);
          level[i * LINE + k] = Math.min(LINE_LEVELS - 1, Math.floor(v * LINE_LEVELS));
        }
      }
    }
    // Дальние — первыми: сортировка трёх без аллокаций.
    order[0] = 0;
    order[1] = 1;
    order[2] = 2;
    for (let a = 0; a < 2; a++) {
      for (let b = a + 1; b < 3; b++) {
        if (planets[order[b]! * 4 + 2]! < planets[order[a]! * 4 + 2]!) {
          const tmp = order[a]!;
          order[a] = order[b]!;
          order[b] = tmp;
        }
      }
    }

    if (!tiny) {
      g.globalAlpha = 0.7 + 0.3 * breath;
      stamp(g, s.halo, 0, 0, 0.93 + 0.1 * breath);
      drawLines(false, fade);
    }

    // Перицентр дальше ядра, поэтому над его диском планета всегда
    // явно за ним или явно перед ним: за — рисуется до ядра и закрыта им.
    for (let n = 0; n < 3; n++) {
      const i = order[n]!;
      const z = planets[i * 4 + 2]!;
      if (z < 0) drawPlanet(s, i, (0.35 + 0.65 * nearness(z)) * fade, tiny);
    }

    const core = 0.965 + 0.06 * breath;
    if (!tiny) {
      g.globalAlpha = 0.7 + 0.3 * spark;
      stamp(g, s.flare, 0, 0, core * LOADER.flare * (0.94 + 0.08 * spark), core * LOADER.flare);
    }
    g.globalAlpha = 1;
    stamp(g, tiny ? s.tinyCore : s.core, 0, 0, core);

    if (!tiny) drawLines(true, fade);
    for (let n = 0; n < 3; n++) {
      const i = order[n]!;
      const z = planets[i * 4 + 2]!;
      if (z >= 0) drawPlanet(s, i, (0.35 + 0.65 * nearness(z)) * fade, tiny);
    }
    g.globalAlpha = 1;

    if (visible) raf = requestAnimationFrame(frame);
  }

  function resume(): void {
    if (!raf && visible) raf = requestAnimationFrame(frame);
  }

  let io: IntersectionObserver | null = null;
  if (typeof IntersectionObserver !== 'undefined') {
    io = new IntersectionObserver((entries) => {
      visible = entries.some((e) => e.isIntersecting);
      if (visible) resume();
    });
    io.observe(host);
  }
  let ro: ResizeObserver | null = null;
  if (typeof ResizeObserver !== 'undefined') {
    ro = new ResizeObserver(() => resume());
    ro.observe(host);
  }

  // Первый кадр — сразу, до отрисовки страницы: заставка index.html сменяется
  // индикатором без пустого кадра.
  frame(performance.now());

  return () => {
    visible = false;
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
    io?.disconnect();
    ro?.disconnect();
  };
}
